import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'ShopifyIntegration',
  data () {
    return {
      isLoading: false,
      blogIdLoading: false,
      isDeleting: false,
      shopifyStore: '',
      selectedBlogId: null,
      blogs: [],
      rules: {
        required: value => !!value || 'Required.',
        validShopifyStore: value => {
          const pattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/
          return pattern.test(value) || 'Please enter a valid Shopify store URL (your-store.myshopify.com)'
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'shopifyIntegration'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    // Check if the currently active integration is Shopify
    activeIntegrationStore () {
      return this.shopifyIntegration?.shopify_store
    },
    isValidToInitiateAuth () {
      // Check if store name is valid to start OAuth flow
      return this.rules.validShopifyStore(this.shopifyStore) === true
    },
    redirectUri () {
      // Build the redirect URI for OAuth
      return `${window.location.origin}/shopify-auth-callback`
    },
    isIntegrationComplete () {
      return this.activeIntegrationStore && this.selectedBlogId
    },
    savedBlogId () {
      const blogId = this.shopifyIntegration?.shopify_blog_id
      const blog = this.blogs?.find(blog => Number(blog.id) === Number(blogId))
      return blog?.title
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'initiateShopifyAuth',
      'saveShopifyIntegration',
      'getShopifyBlogs',
      'deleteIntegration'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    async startAuthFlow () {
      if (!this.isValidToInitiateAuth) return

      this.isLoading = true
      try {
        const normalizedStore = this.shopifyStore.trim().toLowerCase()

        // Store the contentplan ID in session storage for the callback
        sessionStorage.setItem('shopify_store', normalizedStore)
        sessionStorage.setItem('shopify_contentplan_id', this.contentplanId)
        sessionStorage.setItem('active_workspace_id', this.$route.params.workspaceId)

        // Call the backend to initiate OAuth flow
        const response = await this.initiateShopifyAuth({
          shopifyStore: normalizedStore,
          contentplanId: this.contentplanId,
          redirectUri: this.redirectUri
        })

        // If response contains an auth URL, redirect to it
        if (response && response.authUrl) {
          window.location.href = response.authUrl
        } else {
          throw new Error('No authorization URL returned from server')
        }
      } catch (error) {
        console.error('Error initiating Shopify OAuth:', error)
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not initiate Shopify authentication'}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    initializeFromIntegration (integration) {
      if (integration?.type === 'shopify') {
        this.shopifyStore = integration.shopify_store || ''
        this.selectedBlogId = integration.shopify_blog_id || null
      } else {
        // Reset fields if no integration or not shopify
        this.shopifyStore = ''
        this.selectedBlogId = null
        this.blogs = []
      }
    },
    async fetchShopifyBlogs () {
      if (!this.contentplanId) return

      this.blogIdLoading = true
      try {
        const response = await this.getShopifyBlogs({ contentplanId: this.contentplanId })
        if (!response?.blogs) throw new Error('No blogs found')
        this.blogs = response.blogs
      } catch (error) {
        console.error('Error fetching Shopify blogs:', error)
        this.setSnackbar({
          open: true,
          text: `Error fetching blogs: ${error?.response?.data?.message || error.message}`,
          type: 'error'
        })
      }

      this.blogIdLoading = false
    },
    async saveBlogSelection () {
      if (!this.selectedBlogId) {
        this.setSnackbar({
          open: true,
          text: 'Please select a blog',
          type: 'error'
        })
        return
      }

      this.isLoading = true
      try {
        await this.saveShopifyIntegration({
          contentplanId: this.contentplanId,
          type: 'shopify',
          shopifyStore: this.shopifyStore,
          shopifyBlogId: this.selectedBlogId
        })

        this.setSnackbar({
          open: true,
          text: 'Blog selection saved successfully',
          type: 'success'
        })

        // Refresh integration data
        await this.getIntegrations({ contentplanId: this.contentplanId })
      } catch (error) {
        console.error('Error saving blog selection:', error)
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleDelete () {
      if (!this.activeIntegrationStore || this.isDeleting) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isDeleting = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'shopify'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId, workspaceId: this.$route.params.workspaceId }
        })
      } catch (error) {
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`, type: 'error' })
      } finally {
        this.isDeleting = false
      }
    }
  },
  async mounted () {
    await Promise.all([
      this.getIntegrations({ contentplanId: this.contentplanId }),
      this.fetchShopifyBlogs()
    ])
    this.initializeFromIntegration(this.shopifyIntegration)

    const pendingShop = sessionStorage.getItem('shopify_pending_shop')
    if (pendingShop && !this.activeIntegrationStore) {
      this.shopifyStore = pendingShop
      sessionStorage.removeItem('shopify_pending_shop')
    }
  }
}
